import { useEffect, useRef } from "react";
import { useUser } from "@clerk/react";
import { api } from "@/lib/api";
import { trackPiecanHandoffClick } from "@/services/analytics";

const PIECAN_HOME = "https://piecan.ai/image-to-video";

/**
 * Single email-friendly handoff link: https://animepup.com/go/piecan
 *
 * Backdoor: every visitor who comes through this link is sent to PieCan with a
 * fresh, single-use token that grants acc_tier 1 — no sign-in required and no
 * eligibility gate. The token is minted server-side (/partner/piecan-backdoor)
 * with a unique source id per click, so PieCan's first-claim-wins dedup never
 * blocks a later visitor, and there is no email-expiry concern.
 */
export function PiecanRedirectPage() {
  const { user } = useUser();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      let dest = PIECAN_HOME;
      try {
        const res = await api.get<{ success: boolean; url?: string }>(
          "/partner/piecan-backdoor",
        );
        if (res?.url) dest = res.url;
      } catch {
        // Network/endpoint hiccup → still send them to PieCan (without the grant).
      }
      trackPiecanHandoffClick(user?.id ?? null, "email_redirect");
      window.location.replace(dest);
    })();
  }, [user?.id]);

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
      <div className="size-10 border-2 border-[#d4a574]/30 border-t-[#d4a574] rounded-full animate-spin mb-4" />
      <p className="text-[15px] font-semibold text-foreground">Taking you to PieCan…</p>
      <p className="text-[13px] text-muted-foreground mt-1">One moment.</p>
    </div>
  );
}
